// SEQ-240: "How is this worked out" text for the Dashboard's Retention
// Analytics section. Edit this file whenever those calculations change.
//
// WHAT IT DESCRIBES (line numbers at commit 3eed3d4):
// - useRetentionStats.ts 130-134: active projects only (status active or blank).
// - useRetentionStats.ts 146-160: per claim report, held is the retention in
//   the claim's payment reconciliation summary when one exists, otherwise
//   claims_reports.retention_amount (getOperationalRetentionAmount,
//   reconciliationSummary.ts 228-235); received is retention_received_amount.
// - useRetentionStats.ts 168-206: per project totals, only projects with
//   held above zero, sorted by outstanding.
// - useRetentionStats.ts 210-245: the cumulative trend by claim month.
// - useRetentionStats.ts 247-274: aging, each claim's own outstanding grouped
//   by whole months since its claim month (0-3, 4-6, 7-12, 13 and over).
// - useRetentionStats.ts 276-324: the per-project monthly trend.
// - useRetentionStats.ts 338-343: the four card totals.
// - RetentionDashboardSection.tsx 111-136: with a filter set, the per-project
//   figures and the cards are narrowed to the filtered projects; the trend and
//   aging charts are not narrowed. The date filter never applies here.
// - RetentionDashboardCharts.tsx: RetentionByProjectChart shows the top 10, bottom 10 or all projects (Show menu).
//
// dashboardFigureDefinitions.test.ts checks every id here is used by a
// component and every id a component uses is here.

import type { MetricDefinition } from "../components/howWorkedOut/metricDefinition";

const FROM_CLAIMS =
  "From the progress claims. Held uses the retention in the claim's payment reconciliation when there is one, otherwise the retention on the claim. Received is what was recorded as received back on each claim.";

const HELD_LINE =
  "Held: for each claim, the retention in its payment reconciliation when there is one, otherwise the retention on the claim.";
const RECEIVED_LINE = "Received: the retention recorded as received back on each claim.";
const ONLY_HELD_LINE = "Only projects that have held some retention count.";

export const RETENTION_FIGURES = {
  "retention.held": {
    name: "Retention held",
    meaning: "All the retention held back on the claims of the projects in view.",
    formula: ["Adds up the retention held on every claim, all time.", HELD_LINE, "It does not take off what has been paid back."],
    source: { kind: "calculated", detail: FROM_CLAIMS },
    projects: { scope: "active-only", detail: ONLY_HELD_LINE },
    dates: { scope: "all-time" },
    period: "Every claim ever made, up to today.",
  },
  "retention.received": {
    name: "Retention received",
    meaning: "The retention the builder has paid back so far.",
    formula: ["Adds up the retention received back on every claim, all time.", RECEIVED_LINE],
    source: { kind: "calculated", detail: FROM_CLAIMS },
    projects: { scope: "active-only", detail: ONLY_HELD_LINE },
    dates: { scope: "all-time" },
    period: "Every claim ever made, up to today.",
  },
  "retention.outstanding": {
    name: "Retention outstanding",
    meaning: "Retention earned but not yet paid back.",
    formula: ["Retention held minus retention received.", HELD_LINE, RECEIVED_LINE],
    source: { kind: "calculated", detail: FROM_CLAIMS },
    projects: { scope: "active-only", detail: ONLY_HELD_LINE },
    dates: { scope: "all-time" },
    period: "Every claim ever made, up to today.",
  },
  "retention.recovery-rate": {
    name: "Recovery rate",
    meaning: "How much of the retention held has been paid back.",
    formula: [
      "Retention received divided by retention held, times 100.",
      "The card turns green at 70% or more, amber from 30%, red below 30%.",
    ],
    source: { kind: "calculated", detail: FROM_CLAIMS },
    projects: { scope: "active-only", detail: ONLY_HELD_LINE },
    dates: { scope: "all-time" },
    period: "Every claim ever made, up to today.",
  },
  "retention.overview": {
    name: "Retention overview",
    meaning: "The split of retention held into what has come back and what is still outstanding.",
    formula: ["Received and outstanding as two slices of the retention held.", "Outstanding: held minus received.", HELD_LINE],
    source: { kind: "calculated", detail: FROM_CLAIMS },
    projects: { scope: "active-only", detail: ONLY_HELD_LINE },
    dates: { scope: "all-time" },
    period: "Every claim ever made, up to today.",
  },
  "retention.recovery-gauge": {
    name: "Recovery rate gauge",
    meaning: "How much of the retention held has been paid back, as a dial.",
    formula: [
      "Retention received divided by retention held, times 100. The dial stops at 100%.",
      "Green at 70% or more, amber from 30%, red below 30%.",
    ],
    source: { kind: "calculated", detail: FROM_CLAIMS },
    projects: { scope: "active-only", detail: ONLY_HELD_LINE },
    dates: { scope: "all-time" },
    period: "Every claim ever made, up to today.",
  },
  "retention.by-project": {
    name: "Retention by project",
    meaning: "Retention received and still outstanding for the projects with the most outstanding (top ten by default).",
    formula: [
      "For each project: received, and held minus received.",
      "Sorted by outstanding, largest first. Shows the top ten by default, with a Show menu for the bottom ten or all projects. Click a bar to open that project.",
    ],
    source: { kind: "calculated", detail: FROM_CLAIMS },
    projects: { scope: "active-only", detail: ONLY_HELD_LINE },
    dates: { scope: "all-time" },
    period: "Every claim ever made, up to today.",
  },
  "retention.trend": {
    name: "Retention trend",
    meaning: "How retention held, received and outstanding have built up over time.",
    formula: [
      "For each claim month, adds that month's held and received retention to the running totals.",
      "Outstanding: running held minus running received.",
      "Months where nothing was held or received are skipped.",
    ],
    source: { kind: "calculated", detail: FROM_CLAIMS },
    projects: { scope: "active-ignores-filters" },
    dates: { scope: "all-time" },
    period: "From the first claim with retention to the latest, by claim month.",
  },
  "retention.aging": {
    name: "Retention aging",
    meaning: "Outstanding retention grouped by how long ago its claim was made.",
    formula: [
      "For each claim with retention still outstanding (held minus received above zero), counts the whole months from its claim month to this month.",
      "Groups: 0 to 3 months, 4 to 6, 7 to 12, and 13 or more. The bars add up the outstanding in each group.",
      "Hover a bar to see how many claims are in it.",
    ],
    source: { kind: "calculated", detail: FROM_CLAIMS },
    projects: { scope: "active-ignores-filters" },
    dates: { scope: "all-time", detail: "Age is counted from today." },
    period: "Every claim with retention outstanding today.",
  },
  "retention.project-trend": {
    name: "Per-project retention trend",
    meaning: "One project's retention, month by month.",
    formula: [
      "Bars: the retention held and received for each claim month of the project you pick.",
      "Lines: the running totals of held and received.",
      "Months where nothing was held or received are skipped.",
    ],
    source: { kind: "calculated", detail: FROM_CLAIMS },
    projects: { scope: "active-only", detail: "The project list follows the filters; pick one from the list." },
    dates: { scope: "all-time" },
    period: "Every claim month of the chosen project.",
  },
} satisfies Record<string, MetricDefinition>;
